import { H1, H2, P, UL, LI, Code, Note, Warn, StepList } from './primitives';

export function SectionAgentRoles() {
  return (
    <>
      <H1>Agent Roles</H1>
      <P>
        Two roles ship with the platform: the <strong>Worker</strong> (runs
        engineering tasks — one-off issues and scheduled loops) and the{' '}
        <strong>Product Manager</strong> (triages feedback into planned
        tickets). A role is a system prompt, an optional tool allow-list, and
        an optional gate command — hardcoded in the platform, not
        operator-editable.
      </P>

      <H2>Worker</H2>
      <P>
        The full-toolkit agent. It reads its mission, the issue board, and the
        repo, then either implements an issue (→ PR), files new issues, or
        manages the board. Its gate is conditional — the test suite runs only
        when the firing actually changed the repo, so pure board-management
        firings pass untouched.
      </P>

      <Warn>
        <strong>Loop budget:</strong> if a gate keeps failing, the run retries
        up to <em>max loops</em> times (5), then either fails immediately
        (gate mode <Code>strict</Code>) or opens the PR anyway with a warning
        (gate mode <Code>warn</Code>). Set the gate mode on the project's Team
        tab.
      </Warn>

      <H2>Stage statuses</H2>
      <UL>
        <LI><Code>pending</Code> — not yet started.</LI>
        <LI><Code>running</Code> — agent actively working.</LI>
        <LI><Code>succeeded</Code> — stage completed successfully.</LI>
        <LI>
          <Code>awaiting_gate</Code> — legacy: older runs paused here for a
          human decision. New runs never enter it; an exhausted gate fails.
        </LI>
        <LI><Code>failed</Code> — stage errored, or its gate budget ran out.</LI>
        <LI><Code>skipped</Code> — role was not in the pipeline.</LI>
      </UL>
    </>
  );
}

export function SectionLoopGates() {
  return (
    <>
      <H1>Gates &amp; Retries</H1>
      <P>
        A pipeline stage can define a <strong>gate</strong> — a shell command
        that must exit 0 before the pipeline advances. When a gate fails, the
        run does not stop: it <strong>automatically loops back</strong> to the
        stage with the gate's failure output fed in as context, and tries
        again.
      </P>

      <H2>The loop budget</H2>
      <P>
        Every run has a <strong>max loops</strong> budget of 5 automatic
        retries. Every gate failure consumes one unit. Clean runs, where no
        gate ever fails, never loop and finish on their own.
      </P>

      <H2>When the budget runs out</H2>
      <P>
        What happens next depends on the project's <strong>gate mode</strong>{' '}
        (Team tab, owner-only). In <Code>strict</Code> mode (the default) the
        run <strong>fails immediately</strong> — no PR is opened and nothing
        waits for a human. The failure reason carries the tail of the gate
        output, so the timeline shows exactly what still fails. In{' '}
        <Code>warn</Code> mode the run logs a gate warning and carries on, so
        the PR is still opened with a note that tests did not pass.
      </P>

      <H2>What the next attempt sees</H2>
      <P>
        The failing gate's exact output (test failures, build errors, etc.) is
        carried forward into the next attempt so the agent can converge
        instead of repeating the same mistake.
      </P>

      <Note>
        The Worker role's gate is conditional: the test suite runs only when
        the session actually changed the repository — a loop firing that only
        managed the issue board passes its gate untouched.
      </Note>
    </>
  );
}

export function SectionIteration() {
  return (
    <>
      <H1>Iterating on Runs</H1>
      <P>
        After a run finishes, you can hand the same issue back to the agents
        with feedback. Iteration continues on the <em>same branch</em> (and
        PR, if open), so the agents refine their work instead of starting
        over.
      </P>

      <H2>How to iterate</H2>
      <StepList
        steps={[
          {
            n: 1,
            title: 'Open a finished run',
            body: (
              <p>
                Iteration is available on terminal runs that produced a
                branch.
              </p>
            ),
          },
          {
            n: 2,
            title: 'Find the feedback box at the end of the Logs timeline',
            body: (
              <p>
                Scroll the run's <strong>Logs</strong> tab to the bottom — the
                iterate box sits under the last timeline entry.
              </p>
            ),
          },
          {
            n: 3,
            title: 'Write feedback',
            body: (
              <p>
                Describe what should change. Be specific about what you
                reviewed and what needs to be different.
              </p>
            ),
          },
          {
            n: 4,
            title: 'Submit',
            body: (
              <p>
                A new run starts on the same branch, and you're taken straight
                to it. The child run links back to its parent in the header.
              </p>
            ),
          },
        ]}
      />

      <H2>What carries forward</H2>
      <UL>
        <LI>The original brief and pipeline settings.</LI>
        <LI>
          The branch from the previous run — agents continue where it left
          off, and an open PR simply gains commits.
        </LI>
        <LI>Your feedback, injected into every stage's context.</LI>
      </UL>

      <Note>
        Iteration shines when the agents got 80% right but missed a
        requirement, or when your code review surfaced specific improvements.
      </Note>
    </>
  );
}

export function SectionAutoMerge() {
  return (
    <>
      <H1>Auto-merge &amp; Risk</H1>
      <P>
        Every successful run's diff is classified <strong>LOW</strong> or{' '}
        <strong>HIGH</strong> risk by a fixed rubric — no configuration, no
        knobs. Risk is recorded for audit on the run and in the PR body; it
        never holds a PR. A PR whose test gate passed merges itself as soon as
        GitHub reports it mergeable.
      </P>

      <H2>The risk rubric</H2>
      <P>A diff is HIGH risk when any of these hold:</P>
      <UL>
        <LI>Any file was deleted.</LI>
        <LI>
          It touches operator-coupled or security-sensitive paths:
          auth/security/crypto code, environment files, core configuration,
          Dockerfiles/compose, CI workflows, or the Makefile.
        </LI>
        <LI>It is a broad refactor: more than 20 files or 500 changed lines.</LI>
      </UL>
      <P>
        Everything else — gate-verified application code, dependencies, schema
        changes, UI work — is LOW risk. The test gate is the quality lever; a
        HIGH label tells you where to look afterwards, it does not stop the
        merge. On the platform's own repository, the Worker's test gate must
        pass before merge; the image build also type-checks the code and
        runs the test suites, so a red main publishes no image.
      </P>

      <H2>The merge decision</H2>
      <P>A PR auto-merges when all three are true:</P>
      <UL>
        <LI>The test gate passed.</LI>
        <LI>The run verdict passed (the pipeline finished successfully).</LI>
        <LI>GitHub reports the PR mergeable (no conflicts).</LI>
      </UL>
      <P>
        Auto-merged PRs are squash-merged, the branch is deleted, and the
        issue moves to <Code>closed</Code>. Held PRs get status{' '}
        <Code>awaiting-review</Code> with the specific hold reason.
      </P>
      <P>
        For a PR with a passing gate, harmless GitHub states don't
        cause a hold. If the branch is <Code>behind</Code> the base (it moved
        while the agent worked), the branch is updated from the base and
        checked again. If only non-required checks are failing (
        <Code>unstable</Code>), the PR still merges. If the PR is{' '}
        <Code>blocked</Code> waiting on required checks, GitHub&apos;s native
        auto-merge is turned on. Held PRs are checked again by the
        periodic PR sync and merge on their own once they clear. Real
        conflicts are held as <em>Merge conflict with base — needs rebase</em>.
      </P>

      <H2>Where you see and act on it</H2>
      <UL>
        <LI>
          <strong className="text-ink">Run page</strong> — a green callout for
          auto-merged PRs; an amber callout with the hold reason and a{' '}
          <strong>Merge PR</strong> button for held ones. Risk is also shown
          in the details rail.
        </LI>
        <LI>
          <strong className="text-ink">Issue rows and pages</strong> — a{' '}
          <em>Merge ready</em> badge, and a Merge button on the issue header.
        </LI>
        <LI>
          <strong className="text-ink">The PR itself</strong> — a risk
          assessment block (🟢/🔴, justification, action taken) appended to
          the body.
        </LI>
      </UL>

      <Note>
        Merging from the app records who merged and when on the issue
        timeline — the audit trail stays complete even when you take over.
      </Note>
    </>
  );
}
